/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { ThemeProvider, jsx } from '@emotion/react';

import * as React from 'react';
import { type ReactNode, useMemo } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { SearchExplainProvider } from 'bundles/search-common/components/internal-search-explainer/SearchExplainContext';
import useSearchExplainMetrics from 'bundles/search-common/components/internal-search-explainer/hooks/useSearchExplainMetrics';
import useSearchExplainState from 'bundles/search-common/components/internal-search-explainer/hooks/useSearchExplainState';
import LearnerProvider, { useShowCourseraTier } from 'bundles/search-common/providers/LearnerProvider';
import RouterProvider from 'bundles/search-common/providers/RouterProvider';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { searchTheme } from 'bundles/search-common/searchTheme';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { getSearchConfigs } from 'bundles/search-v2/hooks/useSearchConfigs';
import { getSEOSearchConfigs } from 'bundles/seo-entity-page/hooks/useSEOSearchConfigs';

type Props = {
  children: ReactNode;
  isSEOEntityPage: boolean;
};

const SearchProviderWrapper = ({ children, isSEOEntityPage }: Props) => {
  const userHasCourseraPlus = useShowCourseraTier();
  const searchConfigs = useMemo(() => {
    return isSEOEntityPage ? getSEOSearchConfigs(userHasCourseraPlus) : getSearchConfigs(userHasCourseraPlus);
  }, [isSEOEntityPage, userHasCourseraPlus]);

  return <SearchProvider searchConfigs={searchConfigs}>{children}</SearchProvider>;
};

export const SeoSearchProviderWrapper = ({ children, isSEOEntityPage }: Props) => {
  // Search explain state and metrics
  const { hasPermission, enableSearchExplain, toggleSearchExplain } = useSearchExplainState();
  const location = useLocation();
  const query = inferQueryFromParams(location?.query?.query) || '';
  const {
    metrics: queryMetrics,
    loading: metricsLoading,
    error: metricsError,
  } = useSearchExplainMetrics(query, enableSearchExplain);

  const searchExplainContextValue = useMemo(
    () => ({
      hasPermission,
      enableSearchExplain,
      toggleSearchExplain,
      queryMetrics,
      metricsLoading,
      metricsError: metricsError || undefined,
    }),
    [hasPermission, enableSearchExplain, toggleSearchExplain, queryMetrics, metricsLoading, metricsError]
  );

  return (
    <ThemeProvider theme={searchTheme}>
      <RouterProvider>
        <LearnerProvider>
          <SearchProviderWrapper isSEOEntityPage={isSEOEntityPage}>
            <SearchExplainProvider value={searchExplainContextValue}>{children}</SearchExplainProvider>
          </SearchProviderWrapper>
        </LearnerProvider>
      </RouterProvider>
    </ThemeProvider>
  );
};
